import React, { useState, useEffect, useCallback } from 'react';
import { User, CalendarDays, FileText, Download, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, Spinner } from '../common';
import { StatusMover } from '../common/StatusMover';
import { getActivityDetail, updateDossierActivity } from '../../services/dossier.service';
import { downloadAttachmentFile } from '../../services/attachments.service';
import type { ActivityDetail, ActivityDocument, DossierActivity } from '../../types';

type ActivityStatus = DossierActivity['status'];

interface Props {
  activityId: number;
  onClose: () => void;
  // Notifică părintele când statusul a fost schimbat din modal (refresh listă).
  onStatusChange?: (status: ActivityStatus) => void;
}

// Formatare dată ro-RO (fără oră). Întoarce '' pentru valori lipsă/invalide.
const formatRoDate = (v?: string | null): string => {
  if (!v) return '';
  const d = new Date(String(v).substring(0, 10));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Dimensiune fișier lizibilă (KB/MB), tolerantă la valori lipsă.
const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ActivityDetailModal({ activityId, onClose, onStatusChange }: Props) {
  const [detail, setDetail] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getActivityDetail(activityId);
      setDetail(data);
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [activityId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const handleStatusChange = async (newStatus: ActivityStatus) => {
    if (!detail || detail.status === newStatus || savingStatus) return;
    setSavingStatus(true);
    try {
      await updateDossierActivity(detail.dossier_id, detail.id, { status: newStatus });
      setDetail(prev => (prev ? { ...prev, status: newStatus } : prev));
      onStatusChange?.(newStatus);
      toast.success('Statusul a fost actualizat.');
    } catch (err) {
      console.error(err);
      toast.error('Statusul nu a putut fi actualizat. Încearcă din nou.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDownload = (doc: ActivityDocument) => {
    // Preferăm download_url dacă backend-ul îl furnizează; altfel construim ruta
    // standard de descărcare pe attachment_id (ca în restul aplicației).
    if (doc.download_url) {
      window.open(doc.download_url, '_blank', 'noopener,noreferrer');
    } else {
      downloadAttachmentFile(doc.attachment_id, doc.filename || `atasament-${doc.attachment_id}`);
    }
  };

  const renderSourceBadge = () => {
    if (!detail) return null;
    const label = detail.source_wp_id
      ? `Din OpenProject #${detail.source_wp_id} · ${detail.project_name}`
      : detail.project_name;
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
        {label}
      </span>
    );
  };

  const dateRange = detail ? [formatRoDate(detail.start_date), formatRoDate(detail.due_date)] : ['', ''];
  const hasDates = dateRange[0] || dateRange[1];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={loading ? 'Detalii activitate' : (detail?.name || 'Detalii activitate')}
      size="lg"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : loadError || !detail ? (
        <div className="flex flex-col items-center text-center py-10">
          <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
          <p className="text-sm font-medium text-gray-800">Detaliile nu au putut fi încărcate</p>
          <p className="text-sm text-gray-500 mt-1">A apărut o eroare. Încearcă din nou.</p>
          <button
            onClick={loadDetail}
            className="mt-4 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Reîncearcă
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Sursă */}
          <div>{renderSourceBadge()}</div>

          {/* Descriere */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Descriere</h4>
            {detail.description ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">Fără detalii</p>
            )}
          </div>

          {/* Responsabil & Termene */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Responsabil</h4>
              <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                <User className="w-4 h-4 text-gray-400" />
                {detail.responsible_name || <span className="text-gray-400">Nealocat</span>}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Termene</h4>
              <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                {hasDates ? (
                  <span>
                    {dateRange[0] || '—'}
                    {' → '}
                    {dateRange[1] || '—'}
                  </span>
                ) : (
                  <span className="text-gray-400">Fără termene</span>
                )}
              </div>
            </div>
          </div>

          {/* Documente */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Documente atașate {detail.documents.length > 0 && `(${detail.documents.length})`}
            </h4>
            {detail.documents.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Niciun document atașat</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {detail.documents.map(doc => {
                  const size = formatFileSize(doc.filesize);
                  return (
                    <button
                      key={doc.attachment_id}
                      onClick={() => handleDownload(doc)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors group"
                      title="Descarcă"
                    >
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 min-w-0 text-sm text-gray-700 truncate">{doc.filename}</span>
                      {size && <span className="text-xs text-gray-400 flex-shrink-0">{size}</span>}
                      <Download className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Schimbare status inline (butoane ◀ ▶ + badge) */}
          <div className="pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</h4>
            <StatusMover
              value={detail.status}
              onChange={handleStatusChange}
              disabled={savingStatus}
              showLabel
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
